import { type Component, For, Show, createSignal } from "solid-js";
import { MatchModal } from "./MatchModal";

export interface StackItem {
  /** Stable identity (slug or "{slug}#{section}") used for keyed updates. */
  key: string;
  /** Small uppercase label shown above the title. */
  eyebrow: string;
  /** Episode title. */
  title: string;
  /** Sub-line (date · duration etc.) shown below the title. */
  meta?: string;
  /** Main body — the episode TL;DR. */
  body: string;
  spotifyUrl?: string;
  applePodcastsUrl?: string;
  /** When set, renders a small "matched in {section}: {excerpt}" callout. */
  match?: { section: string; excerpt: string };
}

interface Props {
  items: StackItem[];
  /**
   * Label shown alongside the down-chevron at the bottom of the active card
   * to advertise that there are more cards behind. e.g. "more matches".
   */
  moreLabel?: string;
}

/**
 * Up to three cards in a forward-leaning stack. The active card is full-size
 * and on top; the others peek out below with a small offset, gentle scale-down,
 * and reduced opacity. Clicking a peeking card promotes it to the front.
 */
export const CardStack: Component<Props> = (props) => {
  const [active, setActive] = createSignal(0);
  const [modalItem, setModalItem] = createSignal<StackItem | null>(null);

  // Reset active index whenever the item set changes (e.g. new search).
  const items = () => {
    const list = props.items.slice(0, 3);
    if (active() >= list.length) setActive(0);
    return list;
  };

  return (
    <div class="card-stack-wrap">
      <section class="card-stack" aria-label="Featured episodes">
        <For each={items()}>
          {(item, i) => {
            const depth = () => Math.abs(i() - active());
            const isActive = () => i() === active();
            return (
              <article
                class="stack-card"
                classList={{ active: isActive(), peek: !isActive() }}
                style={{ "--depth": String(depth()) }}
                tabIndex={isActive() ? 0 : -1}
                onClick={() => setActive(i())}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActive(i());
                  }
                }}
                aria-current={isActive() ? "true" : undefined}
              >
                <p class="eyebrow">{item.eyebrow}</p>
                <h3>{item.title}</h3>
                <Show when={item.meta}>
                  <p class="meta">{item.meta}</p>
                </Show>
                <p class="body">{item.body}</p>
                <Show when={item.match}>
                  <button
                    type="button"
                    class="why-matched-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalItem(item);
                    }}
                  >
                    Why this matched →
                  </button>
                </Show>
                <Show when={item.spotifyUrl || item.applePodcastsUrl}>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: anchors inside handle their own keyboard activation */}
                  <div class="links" onClick={(e) => e.stopPropagation()}>
                    <Show when={item.spotifyUrl}>
                      {(url) => (
                        <a href={url()} target="_blank" rel="noreferrer noopener">
                          Spotify
                        </a>
                      )}
                    </Show>
                    <Show when={item.spotifyUrl && item.applePodcastsUrl}>
                      <span class="dot">·</span>
                    </Show>
                    <Show when={item.applePodcastsUrl}>
                      {(url) => (
                        <a href={url()} target="_blank" rel="noreferrer noopener">
                          Apple Podcasts
                        </a>
                      )}
                    </Show>
                  </div>
                </Show>
              </article>
            );
          }}
        </For>
      </section>
      <Show when={items().length > 1}>
        <button
          type="button"
          class="more-pill"
          onClick={() => setActive((active() + 1) % items().length)}
        >
          <span class="chevron" aria-hidden="true">
            ▾
          </span>
          <span class="label">{props.moreLabel ?? "More"}</span>
        </button>
      </Show>
      <Show when={modalItem()}>
        {(item) => (
          <MatchModal
            title={item().title}
            section={item().match?.section ?? ""}
            excerpt={item().match?.excerpt ?? ""}
            onClose={() => setModalItem(null)}
          />
        )}
      </Show>
    </div>
  );
};
